import { useCallback, useMemo, useState } from 'react';
import * as _ from 'lodash';
import { getOperationsBasisMetrics } from '../constants/operationsMetrics';
import useFarmOrSectionGroupData from '../hooks/useFarmOrSectionGroupData';
import {
  useDataTransforms,
  useFarmName,
  useStartYear,
  useYearsArray,
} from '../utils/hooks';
import { ASSIGNMENTS } from '../constants/operations';
import MetricsHeader from '../components/layout/MetricsHeader';
import Content from '../components/layout/Content';
import DetailsSection from '../components/layout/DetailsSection';
import ItemsTable from '../components/tables/ItemsTable';
import { useFilterContext } from '../contexts/FilterContext';
import { ANNUAL_MULTI_YEAR, N_YEARS } from '../constants/years';
import useOpCategoryTabs from '../hooks/useOpCategoryTabs';
import {
  getOperationItems,
  mergeBasisAggregates,
  occursDuringYearFilter,
  toOperationsTableData,
} from '../utils/operations';
import { OperationsBasisFragment, OPS_BASIS_FRAGMENT_NAME } from '../queries';
import { getMetricsForYear, getYearlyMetrics } from '../utils/columns';
import { asYearString } from '../utils/formatters';

const QUERY_NAME = 'OperationsBasisQuery';

const header = 'Operations Cost Basis';
const OperationsBasisView = () => {
  const { msg, data } = useFarmOrSectionGroupData(
    OperationsBasisFragment,
    QUERY_NAME,
    OPS_BASIS_FRAGMENT_NAME,
    [
      { name: 'applyBuffer', typeString: 'Boolean', value: false },
      {
        name: 'applyMargin',
        typeString: 'Boolean',
        value: false,
      },
      {
        name: 'applySubsidy',
        typeString: 'Boolean',
        value: false,
      },
      {
        name: 'assignment',
        typeString: 'String',
        value: ASSIGNMENTS.DEVELOPER,
      },
    ],
  );

  const {
    state: { year: filterYear, years },
  } = useFilterContext();

  const [showCostBreakdown, setShowCostBreakdown] = useState(false);
  const toggleCostBreakdown = useCallback(() => {
    setShowCostBreakdown((value) => !value);
  }, []);
  const farmName = useFarmName();
  const farmStartYear = useStartYear();

  const allYears = useYearsArray(N_YEARS);

  const { activeCategory, opCategoryTabs } = useOpCategoryTabs();

  const [mergedData] = useDataTransforms(data, [mergeBasisAggregates]);

  const [tableData = {}] = useDataTransforms(mergedData, [
    toOperationsTableData,
  ]);

  const cols = [
    ...(filterYear === ANNUAL_MULTI_YEAR
      ? [
          {
            displayString: 'Year',
            accessor: 'year',
          },
        ]
      : []),
    ...getOperationsBasisMetrics(
      activeCategory,
      showCostBreakdown,
      toggleCostBreakdown,
    ),
  ];

  const metricsForYear = getMetricsForYear(cols);

  const yearlyMetrics = getYearlyMetrics(cols);

  const getItems = useMemo(
    () =>
      getOperationItems(
        activeCategory,
        filterYear,
        years,
        occursDuringYearFilter,
        yearlyMetrics,
        farmStartYear,
      ),
    [activeCategory, filterYear, years, farmStartYear, yearlyMetrics],
  );

  return (
    <>
      <MetricsHeader
        showAnnualSingleYearSelect
        showAnnualMultiYearSelect
        showChartButton={false}
      >
        {header} <i>(Developer Operations)</i>
      </MetricsHeader>
      <Content>
        <DetailsSection>
          <ItemsTable
            data={tableData}
            cols={metricsForYear}
            msg={msg}
            getItems={getItems}
            byCrop
            csvFileName={[
              header,
              _.capitalize(activeCategory ?? 'All'),
              asYearString(filterYear, years, allYears),
              farmName,
            ].join(' - ')}
            tableHeader={opCategoryTabs}
          />
        </DetailsSection>
      </Content>
    </>
  );
};

export default OperationsBasisView;
